import { useEffect, useMemo, useState } from 'react';

const QUIZ_STATS_KEY = 'quiz_stats';
const QUIZ_VALIDATIONS_KEY = 'quiz_validations';
const QUIZ_STATS_EVENT = 'quiz-stats-updated';
const QUIZ_VALIDATIONS_EVENT = 'quiz-validations-updated';
const MAX_ATTEMPTS = 50;

function getEmptyStats() {
  return { questions: {} };
}

function readQuizStats() {
  try {
    const rawValue = window.localStorage.getItem(QUIZ_STATS_KEY);
    const parsedValue = rawValue ? JSON.parse(rawValue) : getEmptyStats();
    if (!parsedValue || typeof parsedValue !== 'object') return getEmptyStats();
    if (!parsedValue.questions || typeof parsedValue.questions !== 'object') return getEmptyStats();
    return parsedValue;
  } catch {
    return getEmptyStats();
  }
}

function writeQuizStats(value) {
  window.localStorage.setItem(QUIZ_STATS_KEY, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(QUIZ_STATS_EVENT));
}

function readValidations() {
  try {
    const raw = window.localStorage.getItem(QUIZ_VALIDATIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export function getQuestionStats(questionId) {
  if (!questionId) return [];
  const stats = readQuizStats();
  const intentos = stats.questions[questionId]?.intentos;
  return Array.isArray(intentos) ? intentos : [];
}

export function registerAnswer(questionId, correcto) {
  if (!questionId) return [];
  const stats = readQuizStats();
  const currentAttempts = getQuestionStats(questionId);
  const nextAttempts = [
    ...currentAttempts,
    { correcto: Boolean(correcto), timestamp: Date.now() },
  ].slice(-MAX_ATTEMPTS);
  stats.questions[questionId] = { intentos: nextAttempts };
  writeQuizStats(stats);
  return nextAttempts;
}

export function resetAllStats(keepValidations = true) {
  window.localStorage.removeItem(QUIZ_STATS_KEY);
  if (!keepValidations) {
    window.localStorage.removeItem(QUIZ_VALIDATIONS_KEY);
    window.dispatchEvent(new CustomEvent(QUIZ_VALIDATIONS_EVENT));
  }
  window.dispatchEvent(new CustomEvent(QUIZ_STATS_EVENT));
}

export function toggleQuestionValidation(questionId) {
  if (!questionId) return;
  const validations = readValidations();
  if (validations[questionId]) {
    delete validations[questionId];
  } else {
    validations[questionId] = true;
  }
  window.localStorage.setItem(QUIZ_VALIDATIONS_KEY, JSON.stringify(validations));
  window.dispatchEvent(new CustomEvent(QUIZ_VALIDATIONS_EVENT));
}

export function useQuestionValidation(questionId) {
  const [validated, setValidated] = useState(() => Boolean(readValidations()[questionId]));

  useEffect(() => {
    function refresh() {
      setValidated(Boolean(readValidations()[questionId]));
    }
    window.addEventListener(QUIZ_VALIDATIONS_EVENT, refresh);
    return () => window.removeEventListener(QUIZ_VALIDATIONS_EVENT, refresh);
  }, [questionId]);

  return validated;
}

function buildQuestionSummary(questionId) {
  const intentos = getQuestionStats(questionId);
  const ultimosCincoIntentos = [...intentos].slice(-5).reverse();
  const totalAciertos = intentos.filter((i) => i.correcto).length;
  const totalFallos = intentos.length - totalAciertos;
  const porcentajeAcierto = intentos.length
    ? Math.round((totalAciertos / intentos.length) * 100)
    : 0;
  return { intentos, ultimosCincoIntentos, totalAciertos, totalFallos, porcentajeAcierto };
}

export function useQuestionStats(questionId) {
  const [summary, setSummary] = useState(() => buildQuestionSummary(questionId));

  useEffect(() => {
    function refreshStats() {
      setSummary(buildQuestionSummary(questionId));
    }
    refreshStats();
    window.addEventListener('storage', refreshStats);
    window.addEventListener(QUIZ_STATS_EVENT, refreshStats);
    return () => {
      window.removeEventListener('storage', refreshStats);
      window.removeEventListener(QUIZ_STATS_EVENT, refreshStats);
    };
  }, [questionId]);

  return useMemo(() => summary, [summary]);
}
